import { Component, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
    ComponentsService,
    SIMULATION_GAME_CODE
} from 'src/app/components.service';

export interface ThemeToken {
    key: string;
    label: string;
    description: string;
}

/**
 * Tokeni moraju da se poklapaju sa `SimulationTheme` u
 * simulation-web/src/config/theme.ts i sa `ThemeDto` na backendu.
 */
export const THEME_TOKENS: ThemeToken[] = [
    {
        key: 'accent',
        label: 'Primarna boja',
        description: 'Dugmad, kvote, aktivni tabovi, rezultati'
    },
    {
        key: 'accentHover',
        label: 'Primarna - hover',
        description: 'Hover stanje primarne boje'
    },
    {
        key: 'onAccent',
        label: 'Tekst na primarnoj',
        description: 'Tekst i ikone na primarnoj podlozi'
    },
    {
        key: 'bg',
        label: 'Pozadina stranice',
        description: 'Loading, error i history ekrani'
    },
    {
        key: 'surface',
        label: 'Podloga tiketa',
        description: 'Glavni kontejner tiketa'
    },
    {
        key: 'surface2',
        label: 'Podloga kartica',
        description: 'Kartice, paneli, donja navigacija, dropdown'
    },
    {
        key: 'surface3',
        label: 'Podloga polja',
        description: 'Input polja, progress traka'
    },
    {
        key: 'surface3Hover',
        label: 'Podloga polja - hover',
        description: 'Hover stanje za podlogu polja'
    },
    {
        key: 'headerBg',
        label: 'Pozadina headera',
        description: 'Zaglavlje tiketa'
    },
    { key: 'text', label: 'Tekst', description: 'Osnovna boja teksta' },
    {
        key: 'textMuted',
        label: 'Prigusen tekst',
        description: 'Sekundarne informacije'
    },
    { key: 'success', label: 'Dobitak', description: 'Dobitne selekcije' },
    { key: 'danger', label: 'Gubitak', description: 'Gubitne selekcije' },
    { key: 'border', label: 'Linije', description: 'Linije i okviri' }
];

/** Originalna exefeed paleta - koristi se za "Vrati na default". */
export const DEFAULT_THEME: { [key: string]: string } = {
    accent: '#ff7400',
    accentHover: '#ff8520',
    onAccent: '#ffffff',
    bg: '#151c2c',
    surface: '#171928',
    surface2: '#242736',
    surface3: '#1a2133',
    surface3Hover: '#252a3d',
    headerBg: '#161e37',
    text: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.7)',
    success: '#48c734',
    danger: '#d80027',
    border: '#394057'
};

// #rgb, #rgba, #rrggbb ili #rrggbbaa - 5 i 7 cifara nisu validan CSS.
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_COLOR =
    /^rgba?\(\s*[\d.]+%?\s*[\s,]\s*[\d.]+%?\s*[\s,]\s*[\d.]+%?\s*(?:[\s,/]\s*[\d.]+%?\s*)?\)$/;

@Component({
    selector: 'app-theme',
    templateUrl: './theme.component.html',
    styleUrls: ['./theme.component.scss']
})
export class ThemeComponent implements OnInit {
    clients: { name: string; clientUuid: string }[] = [];
    games: { name: string; gameCode: string }[] = [
        { name: 'Simulations', gameCode: SIMULATION_GAME_CODE }
    ];

    tokens = THEME_TOKENS;

    selectedClientUuid: string | null = null;
    selectedGameCode: string | null = null;

    /** Trenutno editovane vrijednosti, po kljucu tokena */
    theme: { [key: string]: string } = { ...DEFAULT_THEME };

    isLoading = false;
    isSaving = false;
    message: string | null = null;
    messageIsError = false;

    constructor(
        public router: Router,
        private service: ComponentsService,
        private cdRef: ChangeDetectorRef,
        private ngZone: NgZone
    ) {}

    ngOnInit(): void {
        this.loadClients();
    }

    loadClients(): void {
        this.service.getClientNames().subscribe((clients) => {
            this.ngZone.run(() => {
                this.clients = clients.data.map((client) => ({
                    name: client.name,
                    clientUuid: client.clientUuid
                }));
                this.cdRef.markForCheck();
            });
        });
    }

    onClientSelect(clientUuid: string): void {
        this.selectedClientUuid = clientUuid;
        this.clearMessage();
        if (this.selectedGameCode) {
            this.fetchTheme();
        }
    }

    onGameSelect(gameCode: string): void {
        // Only allow SIM games
        if (gameCode !== SIMULATION_GAME_CODE) {
            console.warn('Only SIM games are allowed');
            return;
        }

        this.selectedGameCode = gameCode;
        this.clearMessage();
        if (this.selectedClientUuid) {
            this.fetchTheme();
        }
    }

    fetchTheme(): void {
        if (!this.selectedClientUuid || !this.selectedGameCode) {
            return;
        }

        this.isLoading = true;
        this.service
            .getTheme(this.selectedClientUuid, this.selectedGameCode)
            .subscribe({
                next: (response) => {
                    this.ngZone.run(() => {
                        // Sto klijent nema, popunjava se default-om
                        this.theme = {
                            ...DEFAULT_THEME,
                            ...(response?.theme || {})
                        };
                        this.isLoading = false;
                        if (!response?.theme) {
                            this.setMessage(
                                'Klijent jos nema svoju paletu - prikazane su default boje.',
                                false
                            );
                        }
                        this.cdRef.markForCheck();
                    });
                },
                error: (error) => {
                    this.ngZone.run(() => {
                        console.error('Error fetching theme:', error);
                        this.isLoading = false;
                        this.setMessage('Greska pri ucitavanju boja.', true);
                        this.cdRef.markForCheck();
                    });
                }
            });
    }

    /**
     * <input type="color"> radi samo sa #rrggbb, pa se rgba() i skraceni hex
     * prikazuju kao najbliza puna hex vrijednost.
     */
    colorPickerValue(key: string): string {
        const value = this.theme[key];
        if (value && /^#[0-9a-fA-F]{6}$/.test(value)) {
            return value;
        }
        if (value && /^#[0-9a-fA-F]{3}$/.test(value)) {
            return (
                '#' +
                value
                    .slice(1)
                    .split('')
                    .map((c) => c + c)
                    .join('')
            );
        }
        if (value && /^#[0-9a-fA-F]{8}$/.test(value)) {
            return value.slice(0, 7);
        }
        return '#000000';
    }

    onColorPicked(key: string, value: string): void {
        this.theme[key] = value;
        this.clearMessage();
    }

    onTextChanged(key: string, value: string): void {
        this.theme[key] = value;
        this.clearMessage();
    }

    isValid(key: string): boolean {
        const value = this.theme[key];
        if (typeof value !== 'string') return false;
        const trimmed = value.trim();
        return HEX_COLOR.test(trimmed) || RGB_COLOR.test(trimmed);
    }

    get hasInvalidValues(): boolean {
        return this.tokens.some((token) => !this.isValid(token.key));
    }

    resetToDefault(): void {
        this.theme = { ...DEFAULT_THEME };
        this.setMessage(
            'Vracene su default boje - kliknite Sacuvaj da bi izmjena bila primijenjena.',
            false
        );
    }

    saveTheme(): void {
        if (!this.selectedClientUuid || !this.selectedGameCode) {
            this.setMessage('Izaberite klijenta i igru.', true);
            return;
        }

        if (this.hasInvalidValues) {
            this.setMessage(
                'Neke boje nisu validne. Dozvoljeni su #hex i rgb()/rgba() zapisi.',
                true
            );
            return;
        }

        this.isSaving = true;
        this.service
            .changeTheme(
                this.selectedClientUuid,
                this.selectedGameCode,
                this.theme
            )
            .subscribe({
                next: (response) => {
                    this.ngZone.run(() => {
                        this.isSaving = false;
                        if (response?.success) {
                            this.setMessage(
                                response.message || 'Boje su sacuvane.',
                                false
                            );
                        } else {
                            this.setMessage(
                                response?.message || 'Greska pri cuvanju boja.',
                                true
                            );
                        }
                        this.cdRef.markForCheck();
                    });
                },
                error: (error) => {
                    this.ngZone.run(() => {
                        console.error('Error saving theme:', error);
                        this.isSaving = false;
                        this.setMessage('Greska pri cuvanju boja.', true);
                        this.cdRef.markForCheck();
                    });
                }
            });
    }

    refresh(): void {
        this.fetchTheme();
    }

    resetFilters(): void {
        this.selectedClientUuid = null;
        this.selectedGameCode = null;
        this.theme = { ...DEFAULT_THEME };
        this.clearMessage();
    }

    private setMessage(text: string, isError: boolean): void {
        this.message = text;
        this.messageIsError = isError;
    }

    private clearMessage(): void {
        this.message = null;
        this.messageIsError = false;
    }
}
